import { useCallback, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  approveShareAdjustment,
  cancelShareAdjustment,
  type ProposeShareAdjustmentInput,
  proposeShareAdjustment,
  resolveReconciliationBreak,
  type ShareAdjustmentProposal,
} from 'src/pages/admin/services/vaultService';

import { RECONCILIATION_NS as NS, DEFAULT_REASON_CODE } from './constants';
import {
  type FieldErrors,
  namesBreak,
  newRequestId,
  type ProposalForm,
  reconciliationErrorMessages,
  validateProposal,
  validateResolution,
} from './helpers';

export type ReconciliationMode = 'propose' | 'approve' | 'cancel';

export interface ActionAlert {
  severity: 'error';
  messages: string[];
}

const EMPTY_PROPOSAL: ProposalForm = {
  shareNeutral: true,
  holderId: '',
  deltaShares: '',
  reasonCode: DEFAULT_REASON_CODE,
  memo: '',
  evidenceRef: '',
  breakValuedAt: '',
  breakAttemptSeq: '',
};

/**
 * XBIT-4864: the three confirms of the Operations queue — propose an ADJ, approve & resolve (a second, distinct operator;
 * step-up), cancel. One request at a time; whatever the answer, the lists are read again (onSettled), so the page shows what
 * the engine now says. Approving a proposal that names a break is the BFF's resolve route (cause + evidence, one engine
 * transaction); a pure correction uses the plain approve. A propose retried with an unchanged body resends its
 * idempotency key; an edited body gets a new one (the same key with another body is a 409).
 */
export const useReconciliationActions = ({
  vaultId,
  onSettled,
}: {
  vaultId: string | undefined;
  onSettled: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [mode, setMode] = useState<ReconciliationMode | null>(null);
  const [target, setTarget] = useState<ShareAdjustmentProposal | null>(null);
  const [proposal, setProposal] = useState<ProposalForm>(EMPTY_PROPOSAL);
  const [cause, setCause] = useState('');
  const [resolveEvidence, setResolveEvidence] = useState('');
  const [errors, setErrors] = useState<FieldErrors<string>>({});
  const [alert, setAlert] = useState<ActionAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const lastSent = useRef<{ key: string; body: string } | null>(null);

  const open = useCallback(
    (next: ReconciliationMode, row?: ShareAdjustmentProposal, preset?: Partial<ProposalForm>) => {
      setProposal({ ...EMPTY_PROPOSAL, ...preset });
      setCause('');
      setResolveEvidence('');
      setErrors({});
      setAlert(null);
      setTarget(row ?? null);
      lastSent.current = null;
      setMode(next);
    },
    []
  );

  const close = useCallback(() => {
    if (!inFlight.current) setMode(null);
  }, []);

  const patchProposal = useCallback(
    (patch: Partial<ProposalForm>) => setProposal((current) => ({ ...current, ...patch })),
    []
  );

  const submit = useCallback(async () => {
    if (inFlight.current || !mode || !vaultId) return;
    let sendPropose: ProposeShareAdjustmentInput | undefined;
    if (mode === 'propose') {
      const invalid = validateProposal(proposal);
      setErrors(invalid);
      if (Object.keys(invalid).length > 0) return;
      const body: Omit<ProposeShareAdjustmentInput, 'idempotencyKey'> = {
        ...(proposal.shareNeutral ? {} : { holderId: proposal.holderId.trim() }),
        deltaShares: proposal.shareNeutral ? '0' : proposal.deltaShares.trim(),
        reasonCode: proposal.reasonCode.trim(),
        memo: proposal.memo.trim(),
        evidenceRef: proposal.evidenceRef.trim(),
        ...(proposal.breakValuedAt.trim()
          ? {
              breakValuedAt: proposal.breakValuedAt.trim(),
              breakAttemptSeq: Number(proposal.breakAttemptSeq.trim()),
            }
          : {}),
      };
      const json = JSON.stringify(body);
      const key = lastSent.current?.body === json ? lastSent.current.key : newRequestId();
      lastSent.current = { key, body: json };
      sendPropose = { ...body, idempotencyKey: key };
    } else if (!target) {
      return;
    } else if (mode === 'approve' && namesBreak(target)) {
      const invalid = validateResolution({ cause, evidenceRef: resolveEvidence });
      setErrors(invalid);
      if (Object.keys(invalid).length > 0) return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setAlert(null);
    try {
      if (sendPropose) await proposeShareAdjustment(vaultId, sendPropose);
      else if (mode === 'cancel')
        await cancelShareAdjustment(vaultId, (target as ShareAdjustmentProposal).id);
      else if (namesBreak(target as ShareAdjustmentProposal)) {
        const row = target as ShareAdjustmentProposal;
        await resolveReconciliationBreak(vaultId, {
          breakValuedAt: row.breakValuedAt as string,
          breakAttemptSeq: row.breakAttemptSeq as number,
          cause: cause.trim(),
          evidenceRef: resolveEvidence.trim(),
          proposalId: row.id,
        });
      } else await approveShareAdjustment(vaultId, (target as ShareAdjustmentProposal).id);
      setMode(null);
      notify(translate(`${NS}.done.${mode}`), { type: 'success' });
    } catch (error) {
      setAlert({ severity: 'error', messages: reconciliationErrorMessages(error, translate) });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  }, [cause, mode, notify, onSettled, proposal, resolveEvidence, target, translate, vaultId]);

  return {
    mode,
    target,
    proposal,
    patchProposal,
    cause,
    setCause,
    resolveEvidence,
    setResolveEvidence,
    errors,
    alert,
    submitting,
    open,
    close,
    submit,
  };
};

export type ReconciliationActionsForm = ReturnType<typeof useReconciliationActions>;
